import { exponentialBackoff, ExponentialBackoffError } from "@/app/_utilities/exponentialBackoff";
import { env } from "@/env";

/** HTTP 208: conversion already in progress — retry with backoff */
class AudioNativeConvertPendingError extends Error {
  constructor() {
    super("Audio native convert pending");
    this.name = "AudioNativeConvertPendingError";
  }
}

/** Match previous behaviour: 2^n second delays, no jitter, no artificial cap */
const AUDIO_NATIVE_BACKOFF = {
  baseDelay: 2000,
  backoffFactor: 2,
  jitter: false as const,
  maxDelay: Number.MAX_SAFE_INTEGER,
} as const;

export const fetchSettings = async (publicUserId: string, projectId: string): Promise<[any, number]> => {
  const response = await fetch(
    `${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/audio-native/${publicUserId}/${projectId}/settings`,
  );
  const json = await response.json();
  return [json, response.status];
};

const CONVERT_TO_PROJECT_RETRIES = 10;

export const convertToProject = async (publicUserId: string, url: string): Promise<[string, boolean]> => {
  try {
    return await exponentialBackoff(
      async () => {
        const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/audio-native/${publicUserId}/convert`, {
          method: "POST",
          body: JSON.stringify({ url }),
          headers: {
            "Content-Type": "application/json",
          },
        });
        const json = await response.json();

        if (response.status === 208) {
          throw new AudioNativeConvertPendingError();
        }

        if (response.status !== 200) {
          throw new Error(json.detail.message);
        }

        return [json.project_id, json.audio_is_or_will_be_available] as [string, boolean];
      },
      {
        ...AUDIO_NATIVE_BACKOFF,
        maxRetries: CONVERT_TO_PROJECT_RETRIES - 1,
        shouldRetry: error => error instanceof AudioNativeConvertPendingError,
      },
    );
  } catch (e) {
    if (e instanceof ExponentialBackoffError) {
      if (e.lastError instanceof AudioNativeConvertPendingError) {
        throw new Error("Could not convert the article to the audio");
      }
      throw e.lastError;
    }
    throw e;
  }
};

const audioExists = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: "HEAD" });
    return response.status === 200;
  } catch {
    return false;
  }
};

export const getAudioUrl = (publicUserId: string, projectId: string, audioPath: string | null): string => {
  return `${env.NEXT_PUBLIC_MEDIA_SERVER_URL}/${audioPath ?? `audio-native/${publicUserId}/${projectId}.mp3`}`;
};

export const waitForAudio = async (
  url: string,
  tries: number = 11,
  afterSecondTry: () => void = () => {},
): Promise<string | ArrayBuffer> => {
  try {
    return await exponentialBackoff(
      async () => {
        const exists = await audioExists(url);
        if (exists) {
          return url;
        }
        throw new Error("Audio not ready");
      },
      {
        ...AUDIO_NATIVE_BACKOFF,
        maxRetries: Math.max(0, tries - 1),
        onRetry: (_error, attempt) => {
          if (attempt === 1 && tries > 1) {
            afterSecondTry();
          }
        },
      },
    );
  } catch (e) {
    if (e instanceof ExponentialBackoffError) {
      throw new Error(`Could not load audio: ${url}`);
    }
    throw e;
  }
};
